import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { ShippingTypesCodes } from '@/data/libraries/ShippingTypes';

import { Button, Col, Container, Form, Row } from 'react-bootstrap';

import { useAppSelector } from '@/ui/store/helperRedux';
import { RootState } from '@/ui/store/store';

import CartController from '@/controllers/CartController';

import { ICart } from '@/domain/interfaces/ICart';
import { IShippingTypes } from '@/domain/interfaces/IProduct';
import { translate } from '@/domain/utils/translate';
import MainTitle from '@/ui/components/MainTitle';
import InputForm from '@/ui/components/forms/InputForm';
import InputMapForm from '@/ui/components/forms/InputMapForm';
import { IStore } from '@/domain/interfaces/IStore';

interface PropsMailingAddress {
  /** Objeto tipo useRef que almacenará la selección de registros */
  formRef: React.RefObject<ICart>;
  /** Función para actualizar el atributo rowSelectionRef  */
  updateFormRef: (newFormRef: ICart) => void;
  /** Función atrás para modificar tipo de entrega */
  returnAction: () => void;
  /** Función para el botón del siguiente paso */
  handleNextButton: (e: MouseEvent) => void;
}

/**
 * Componente para formulario de Direcciones
 * @component
 */
const MailingAddress = ({ handleNextButton, formRef, updateFormRef, returnAction }: PropsWithChildren<PropsMailingAddress>): JSX.Element => {

  /** Referencia al input dirección principal */
  const mainAddressRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

  /** Referencia al input detalles de dirección */
  const addressDetailsRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

  /** Estado de la dirección principal al seleccionar la opción */
  const [address, setAddress] = useState<string>(formRef.current?.clientDetails?.address ?? '');
  /** Estado de la comuna y region al seleccionar la opción */
  const [region, setRegion] = useState<string>(formRef.current?.clientDetails?.region ?? '');

  /**Trae la información del carrito desde Redux */
  const cartProducts = useAppSelector((state: RootState) => state.cartState) as ICart;

  /** State Redux de Tienda */
  const storeData = useAppSelector((state: RootState) => state.storeData) as IStore;

  /** Controlador de carrito */
  const { updateClientInCart } = CartController();

  /** Objeto inicial del carrito */
  const initialCartCheckoutForm: ICart = cartProducts;

  /** Valor del ID de entrega a domicilio */
  const homeDeliveryId: number = ShippingTypesCodes.NATIONAL_SHIPMENT;

  /** Función que setea el lugar a donde hace referencia el input */
  const handleSelect = (address: string, region: string) => {
    setAddress(address);
    setRegion(region);
  };

  /**Trae la información del tipos de entrega desde Redux */
  const shippingTypesState: IShippingTypes[] = useAppSelector((state: RootState) => state.shippingTypes as IShippingTypes[]);
  /** Valida si el tipo de entrega es unico y es envío a domicilio */
  const onlyNationalShipment: boolean = shippingTypesState && shippingTypesState.length === 1 && shippingTypesState[0].id === homeDeliveryId;

  /**Validación de formulario de carrito */
  const handleValidate = () => {
    /** Establece el formulario a actualizar */
    const formCartCheckOut: ICart = formRef.current
      ? JSON.parse(JSON.stringify(formRef.current))
      : JSON.parse(JSON.stringify(initialCartCheckoutForm));
    if (formCartCheckOut.clientDetails) {
      formCartCheckOut.clientDetails.pickupAddress = '';
      formCartCheckOut.clientDetails.shippingType = shippingTypesState.find((shippingType) => shippingType.id === homeDeliveryId) ?? null;
      formCartCheckOut.clientDetails.address = address;
      formCartCheckOut.clientDetails.region = region;
      formCartCheckOut.clientDetails.optionalAddress = addressDetailsRef.current?.value ?? '';
      updateFormRef(formCartCheckOut);
      updateClientInCart(formCartCheckOut.clientDetails);
    }
  };

  const onkeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  /** Evento que envía el formulario cuando se presiona el botón */
  const handleSendInfo = () => {
    handleValidate();
    handleNextButton(new MouseEvent('click'));
  };

  /** UseEffect para validar y traer la información guardada */
  useEffect((): void => {
    if (formRef.current) {
      mainAddressRef.current!.value = formRef.current?.clientDetails?.address ?? '';
      addressDetailsRef.current!.value = formRef.current?.clientDetails?.optionalAddress ?? '';
      handleValidate();
    }
  }, []);

  /**CONSTANTES DE TEXTO */
  const SHIPPING_TEXT: string = translate('checkout.shipping_text');
  const SHIPPING_DESCRIPTION: string = translate('checkout.shipping_description');
  const GO_BACK: string = translate('checkout.update_shipping_type');
  const COUNTRY_LABEL: string = translate('checkout.country_label');
  const STREET_LABEL: string = translate('checkout.street_label');
  const ADDRESS_DETAILS_LABEL: string = translate('checkout.address_details_label');
  const ADDRESS_ALERT: string = translate('checkout.address_alert');
  const CHILE_COUNTRY: string = translate('checkout.chile_country');
  const CHILEAN_AREA: string = translate('checkout.chilean_metropolitan_region');

  const COUNTRY_LABEL_VALUE: string = `${CHILE_COUNTRY}${storeData.onlyChileanRegion ? ` - ${CHILEAN_AREA}` : ''}`;

  return (
    <Container fluid className='p-0'>
      <Row className='g-0 mb-3 p-3 bg-primary-4 rounded rounded-4  shadow'>
        <Col lg={12} className='d-flex text-start'>
          <MainTitle
            titleSize='h2'
            iconName='ico-store'
            iconClass='svg-secondary-1 svg-32 pe-2'
            boldTitle={SHIPPING_TEXT}
            detailsText={SHIPPING_DESCRIPTION}
          />
        </Col>
      </Row>
      <Row className='g-0 mt-4 p-3 bg-primary-4 rounded rounded-4 shadow'>
        <Col lg={5} className='mx-auto my-4'>
          <Form onKeyDown={onkeyDown}>
            <Col>
              <InputForm className='my-2' label={COUNTRY_LABEL} value={COUNTRY_LABEL_VALUE} disabled iconName='ico-error' />
              <InputMapForm
                inputRef={mainAddressRef}
                className='my-2'
                classMain={address === 'error' ? 'form-control-error' : ''}
                errorClass='text-complementary-2 text-nowrap'
                messageError={address === 'error' ? ADDRESS_ALERT : ''}
                onPlaceSelected={handleSelect}
                label={STREET_LABEL}
              />
              <InputForm
                inputRef={addressDetailsRef}
                className='my-2'
                label={ADDRESS_DETAILS_LABEL}
                iconName='ico-error'
              />
            </Col>
            <div className='d-grid gap-2 mt-3'>
              <Button
                className={`btn-primary-icon-standard px-5 ${address === '' || address === 'error' ? 'disabled' : ''}`}
                onClick={handleSendInfo}>
                {translate('checkout.go_ahead')}
              </Button>
            </div>
          </Form>
        </Col>
        {!onlyNationalShipment && (
          <Col
            xs={12}
            className='text-center my-2'
            onClick={returnAction}
          >
            <span className='p-large-medium  link-text-standard'>
              {GO_BACK}
            </span>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default MailingAddress;
